import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  ConnectionTitle,
  Section,
  SectionTitle,
  StatusItem,
  StatusValue,
  StyledDivider,
} from './styles';

import { useAppSelector } from 'features/app/hooks';
import {
  getTrainingForkerQualityGateFlag,
  resolveTrainingForkerQualityGateConfig,
} from 'features/training-forker/config';
import { trainingForkerSelector } from 'features/training-forker/store';

type ThresholdSeverity = 'green' | 'yellow' | 'red' | 'neutral';

const THRESHOLD_COLORS: Record<ThresholdSeverity, string> = {
  green: '#6ee7b7',
  yellow: '#fbbf24',
  red: '#f87171',
  neutral: '#cbd5e1',
};

const WARNING_RATIO = 0.7;

const formatPercent = (value: number | undefined): string =>
  value === undefined ? 'n/a' : `${Math.round(value * 1000) / 10}%`;

const formatSecondsAsMilliseconds = (value: number | undefined): string =>
  value === undefined ? 'n/a' : `${Math.round(value * 1000)}ms`;

const formatDurationMilliseconds = (value: number | undefined): string =>
  value === undefined ? 'n/a' : `${Math.round(value)}ms`;

const formatOptionalNumber = (value: number | undefined): string =>
  value === undefined ? 'n/a' : `${Math.round(value * 100) / 100}`;

const formatOptionalText = (value: string | null | undefined): string =>
  value === undefined || value === null || value === '' ? 'n/a' : value;

const hasMeaningfulQualityLimitation = (
  value: string | null | undefined
): boolean =>
  value !== undefined && value !== null && value !== '' && value !== 'none';

const getSeverityColor = (severity: ThresholdSeverity): string =>
  THRESHOLD_COLORS[severity];

const getRiskSeverity = (
  value: number | undefined,
  threshold: number
): ThresholdSeverity => {
  if (value === undefined || threshold <= 0) {
    return 'neutral';
  }

  const ratio = value / threshold;

  if (ratio >= 1) {
    return 'red';
  }

  if (ratio >= WARNING_RATIO) {
    return 'yellow';
  }

  return 'green';
};

const getBooleanSeverity = (
  isEnabled: boolean,
  isTriggered: boolean
): ThresholdSeverity => {
  if (!isEnabled) {
    return 'neutral';
  }

  return isTriggered ? 'red' : 'green';
};

const buildCurrentLimitText = (
  currentText: string,
  limitText: string
): string => `current ${currentText}, limit ${limitText}`;

const renderStatusRow = (
  label: string,
  value: ReactNode,
  severity: ThresholdSeverity = 'neutral'
) => (
  <StatusItem>
    {label}
    <StatusValue style={{ color: getSeverityColor(severity) }}>
      {value}
    </StatusValue>
  </StatusItem>
);

export const TrainingForkerHealthTab = () => {
  const trainingForkerState = useAppSelector(trainingForkerSelector);
  const flags = useFlags();
  const peers = Object.values(trainingForkerState.qualityMonitorPeers);
  const qualityGateConfig = resolveTrainingForkerQualityGateConfig(
    getTrainingForkerQualityGateFlag(flags)
  );
  const isInGracePeriod = trainingForkerState.qualityMonitorInGracePeriod;

  return (
    <>
      <Section>
        <SectionTitle variant='subtitle2'>Training Forker Health</SectionTitle>
        {renderStatusRow(
          'Forker Enabled:',
          trainingForkerState.enabled ? 'Yes' : 'No',
          trainingForkerState.enabled ? 'green' : 'red'
        )}
        {renderStatusRow(
          'Forker Active:',
          trainingForkerState.active ? 'Yes' : 'No',
          trainingForkerState.active ? 'green' : 'neutral'
        )}
        {renderStatusRow(
          'Quality Degraded:',
          trainingForkerState.qualityDegraded ? 'Yes' : 'No',
          trainingForkerState.qualityDegraded ? 'red' : 'green'
        )}
        {renderStatusRow(
          'Degraded Reason:',
          formatOptionalText(trainingForkerState.qualityDegradedReason),
          trainingForkerState.qualityDegraded ? 'red' : 'neutral'
        )}
        {renderStatusRow(
          'Monitoring Grace Period:',
          formatDurationMilliseconds(qualityGateConfig.monitoringGracePeriodMs),
          'neutral'
        )}
        {renderStatusRow(
          'Grace Period Active:',
          isInGracePeriod ? 'Yes' : 'No',
          isInGracePeriod ? 'yellow' : 'green'
        )}
      </Section>

      <Section>
        <SectionTitle variant='subtitle2'>Peer Health</SectionTitle>
        {peers.length === 0
          ? renderStatusRow('Status:', 'No monitored peers')
          : peers.map((peer) => (
              <Box key={peer.peerId}>
                <ConnectionTitle variant='body2'>{peer.peerId}</ConnectionTitle>
                {renderStatusRow(
                  'Quality State:',
                  peer.qualityDegraded ? 'Degraded' : 'Healthy',
                  peer.qualityDegraded ? 'red' : 'green'
                )}
                {renderStatusRow(
                  'Peer Reason:',
                  formatOptionalText(peer.qualityDegradedReason),
                  peer.qualityDegraded ? 'red' : 'neutral'
                )}
                {renderStatusRow(
                  'Freeze Streak Polls:',
                  buildCurrentLimitText(
                    `${peer.receivedFreezeConsecutivePolls}`,
                    `${qualityGateConfig.received.freeze.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.receivedFreezeConsecutivePolls,
                    qualityGateConfig.received.freeze.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Freeze Streak Duration:',
                  buildCurrentLimitText(
                    formatDurationMilliseconds(
                      peer.receivedFreezeStreakTotalMs
                    ),
                    formatDurationMilliseconds(
                      qualityGateConfig.received.freeze.streakTotalMs
                    )
                  ),
                  getRiskSeverity(
                    peer.receivedFreezeStreakTotalMs,
                    qualityGateConfig.received.freeze.streakTotalMs
                  )
                )}
                {renderStatusRow(
                  'Receive Loss Streak:',
                  buildCurrentLimitText(
                    `${peer.receivedPacketLossConsecutivePolls}`,
                    `${qualityGateConfig.shared.packetLoss.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.receivedPacketLossConsecutivePolls,
                    qualityGateConfig.shared.packetLoss.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Receive Jitter Streak:',
                  buildCurrentLimitText(
                    `${peer.receivedJitterConsecutivePolls}`,
                    `${qualityGateConfig.shared.jitter.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.receivedJitterConsecutivePolls,
                    qualityGateConfig.shared.jitter.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Sent Loss Streak:',
                  buildCurrentLimitText(
                    `${peer.sentPacketLossConsecutivePolls}`,
                    `${qualityGateConfig.shared.packetLoss.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.sentPacketLossConsecutivePolls,
                    qualityGateConfig.shared.packetLoss.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Sent Jitter Streak:',
                  buildCurrentLimitText(
                    `${peer.sentJitterConsecutivePolls}`,
                    `${qualityGateConfig.shared.jitter.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.sentJitterConsecutivePolls,
                    qualityGateConfig.shared.jitter.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Encoder Limitation Streak:',
                  buildCurrentLimitText(
                    `${peer.sentEncoderLimitationConsecutivePolls}`,
                    `${qualityGateConfig.sent.encoderLimitation.minConsecutivePolls}`
                  ),
                  getRiskSeverity(
                    peer.sentEncoderLimitationConsecutivePolls,
                    qualityGateConfig.sent.encoderLimitation.minConsecutivePolls
                  )
                )}
                {renderStatusRow(
                  'Video Bitrate:',
                  peer.videoBitrate === undefined
                    ? 'n/a'
                    : `${peer.videoBitrate} kbps`,
                  'neutral'
                )}
                {renderStatusRow(
                  'Video FPS:',
                  formatOptionalNumber(peer.videoFramesPerSecond),
                  'neutral'
                )}
                {renderStatusRow(
                  'Video Freeze Count:',
                  formatOptionalNumber(peer.videoFreezeCount),
                  'neutral'
                )}
                {renderStatusRow(
                  'Total Freeze Duration:',
                  formatSecondsAsMilliseconds(peer.videoTotalFreezesDuration),
                  'neutral'
                )}
                {peer.receivedVideoPacketLossRate !== undefined &&
                  renderStatusRow(
                    'Receive Packet Loss:',
                    buildCurrentLimitText(
                      formatPercent(peer.receivedVideoPacketLossRate),
                      `${qualityGateConfig.shared.packetLoss.percent}%`
                    ),
                    getRiskSeverity(
                      peer.receivedVideoPacketLossRate * 100,
                      qualityGateConfig.shared.packetLoss.percent
                    )
                  )}
                {peer.receivedVideoJitter !== undefined &&
                  renderStatusRow(
                    'Receive Video Jitter:',
                    buildCurrentLimitText(
                      formatSecondsAsMilliseconds(peer.receivedVideoJitter),
                      `${qualityGateConfig.shared.jitter.ms}ms`
                    ),
                    getRiskSeverity(
                      peer.receivedVideoJitter * 1000,
                      qualityGateConfig.shared.jitter.ms
                    )
                  )}
                {peer.sentVideoPacketLossRate !== undefined &&
                  renderStatusRow(
                    'Sent Packet Loss:',
                    buildCurrentLimitText(
                      formatPercent(peer.sentVideoPacketLossRate),
                      `${qualityGateConfig.shared.packetLoss.percent}%`
                    ),
                    getRiskSeverity(
                      peer.sentVideoPacketLossRate * 100,
                      qualityGateConfig.shared.packetLoss.percent
                    )
                  )}
                {peer.sentVideoJitter !== undefined &&
                  renderStatusRow(
                    'Sent Video Jitter:',
                    buildCurrentLimitText(
                      formatSecondsAsMilliseconds(peer.sentVideoJitter),
                      `${qualityGateConfig.shared.jitter.ms}ms`
                    ),
                    getRiskSeverity(
                      peer.sentVideoJitter * 1000,
                      qualityGateConfig.shared.jitter.ms
                    )
                  )}
                {renderStatusRow(
                  'Encoder Limitation:',
                  formatOptionalText(peer.qualityLimitationReason),
                  getBooleanSeverity(
                    qualityGateConfig.sent.encoderLimitation.enabled,
                    hasMeaningfulQualityLimitation(peer.qualityLimitationReason)
                  )
                )}
                <StyledDivider />
              </Box>
            ))}
      </Section>
    </>
  );
};
